import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { Representative } from '../../model/representative';
import { RepresentativeService } from '../../service/data/representative.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { MessageService } from '../../../core/service/message.service';
import { CommandController } from '../../../core/component/base/command-controller.base';
import { ColumnDefinition } from '../../../core/model/column-definition';
import { MenuItemDefinition } from '../../../core/model/menu-item-definition';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { RepresentativeTableCheckboxComponent } from '../representative-table-checkbox/representative-table-checkbox.component';
import { representativeCommand } from '../../command/representative.command';
import { Command, CommandBase } from '../../../core/model/command';
import { FormGroup } from '@angular/forms';
import { CommandHandlerResolver } from '../../../core/service/command/command-handler-resolver.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { TenantState } from '../../model/const/enums';
import { SendRegistrationKeyRepresentativeCommandHandler } from '../../service/command/send-registration-key-representative-command-handler.service';
import { Subscription, take } from 'rxjs';

@Component({
    selector: 'app-representative-rows',
    templateUrl: './representative-rows.component.html',
    styleUrls: ['./representative-rows.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RepresentativeRowsComponent extends CommandController implements OnInit {
  constructor(
    dispatcher: CommandDispatcher,
    handlerResolver: CommandHandlerResolver,
    private columnFactory: ColumnDefinitionFactory,
    private _representativeService: RepresentativeService,
    private _clipboard: Clipboard,
    private _sendRegistrationKeyCommandHandler: SendRegistrationKeyRepresentativeCommandHandler,
    private _messageService: MessageService
  ) {
    super(dispatcher, handlerResolver);

    this.contextMenuDefinition = [
      new MenuItemDefinition('DELETE_BTN', representativeCommand.deleteRepresentative),
      new MenuItemDefinition('REPRESENTATIVE.MANAGER.CONTEXT_MENU.EDIT', representativeCommand.editRepresentative),
      new MenuItemDefinition(
        'REPRESENTATIVE.MANAGER.CONTEXT_MENU.COPY_CODE_TO_CLIPBOARD',
        representativeCommand.copyCodeToClipboard,
        null,
        (value: Representative) => value.contactState !== TenantState.Registered
      ),
      new MenuItemDefinition(
        'REPRESENTATIVE.MANAGER.CONTEXT_MENU.SEND_INVITATION',
        representativeCommand.sendRegistrationKey,
        null,
        (value: Representative) => value.contactState !== TenantState.Registered
      ),
    ];
  }

  columnDefinition: ColumnDefinition[];
  contextMenuDefinition: MenuItemDefinition[];

  @Input() form: FormGroup;
  @Input() itemsSource: Representative[];
  @Input() isBrf: boolean = false;
  @Output() editRepresentative = new EventEmitter<Representative>();
  private _additionalSub: Subscription;

  ngOnInit() {
    let columns = [
      this.columnFactory.create('TENANT.NAME', 'contactName'),
      this.columnFactory.createComponentBased(
        'REPRESENTATIVE_ROWS.FAULT_NOTIFICATION_ACCESS',
        'faultNotificationsAccess',
        RepresentativeTableCheckboxComponent
      ),
      this.columnFactory.createComponentBased('REPRESENTATIVE_ROWS.INVOICE_ACCESS', 'invoicesAccess', RepresentativeTableCheckboxComponent),
    ];
    if (!this.isBrf) {
      columns = [
        ...columns,
        this.columnFactory.createComponentBased(
          'REPRESENTATIVE_ROWS.DWELLING_INSPECTION_ACCESS',
          'dwellingInspectionsAccess',
          RepresentativeTableCheckboxComponent
        ),
        this.columnFactory.createComponentBased(
          'REPRESENTATIVE_ROWS.LEASE_AGREEMENTS_ACCESS',
          'leaseAgreementsAccess',
          RepresentativeTableCheckboxComponent
        ),
      ];
    }
    columns = [
      ...columns,
      this.columnFactory.createComponentBased(
        'REPRESENTATIVE_ROWS.DOCUMENTS_ACCESS',
        'documentsAccess',
        RepresentativeTableCheckboxComponent
      ),
      this.columnFactory.createTranslation('COMMON.STATUS', 'contactStateCaption'),
    ];

    this.columnDefinition = columns;
  }

  canExecute(command: CommandBase): boolean {
    switch (command.id) {
      case representativeCommand.deleteRepresentative:
      case representativeCommand.editRepresentative:
      case representativeCommand.copyCodeToClipboard:
      case representativeCommand.sendRegistrationKey:
        return true;

      default:
        return false;
    }
  }

  execute(command: CommandBase): void {
    this.clearAdditionalSubscriptions();
    this._additionalSub = new Subscription();
    const representative = <Representative>(<Command>command).context;

    switch (command.id) {
      case representativeCommand.deleteRepresentative:
        this._deleteItem(representative);
        break;
      case representativeCommand.editRepresentative:
        this._editItem(representative);
        break;
      case representativeCommand.copyCodeToClipboard:
        this._copyToClipboard(representative.contactRegistrationKey);
        break;
      case representativeCommand.sendRegistrationKey:
        this._sendRegistrationKey(<Command>command);
        break;
    }
  }

  onRowClick(event) {
    event.id = representativeCommand.editRepresentative;
    this.execute(event);
  }

  protected clearAdditionalSubscriptions(): void {
    this._additionalSub?.unsubscribe();
  }

  private _deleteItem(representative: Representative) {
    this._additionalSub.add(
      this._representativeService.deleteWithConfirmation(representative.id).subscribe(() => {
        this.itemsSource = this.itemsSource.filter(x => x.id !== representative.id);
      })
    );
  }

  private _editItem(representative: Representative) {
    this.editRepresentative.emit(representative);
  }

  private _copyToClipboard(registrationKey: string) {
    this._clipboard.copy(registrationKey);
    this._messageService.showMessage('REPRESENTATIVE.FORM.COPY_TO_CLIPBOARD_MESSAGE');
  }

  private _sendRegistrationKey(command: Command) {
    this._additionalSub.add(this._sendRegistrationKeyCommandHandler.execute(command).subscribe());
  }
}
